import { useIsFetching } from '@tanstack/react-query';
import { useEffect } from 'react';

/**
 * Marks the document once every query has settled.
 *
 * The twin of `usePrerenderSignal` in `apps/web`, which the §5.1 prerenderer
 * waits on. The dashboard is never prerendered, so this exists for the other
 * reader of that attribute: the axe gate.
 *
 * Every accessibility spec here navigates, waits for a heading, and scans — and
 * a heading arrives with the page shell, before any data does. The gate was
 * therefore measuring skeletons, which are grey boxes with nothing to get
 * wrong, and passing every admin screen it had not actually seen. The public
 * site had nineteen serious contrast violations hiding behind exactly that.
 *
 * The attribute name is the web app's, deliberately: one name means the shared
 * `expectNoSeriousAccessibilityViolations` needs no per-app branch.
 */
export function useContentSettled(): void {
  const fetching = useIsFetching();

  useEffect(() => {
    const root = document.documentElement;

    if (fetching > 0) {
      delete root.dataset.prerenderReady;

      return;
    }

    // One frame's grace: `useIsFetching` drops to zero when the last response
    // lands, which is before React has committed the render that uses it.
    const frame = requestAnimationFrame(() => {
      root.dataset.prerenderReady = 'true';
    });

    return () => cancelAnimationFrame(frame);
  }, [fetching]);
}
